import {Component, OnInit, ChangeDetectionStrategy, Inject, AfterViewInit} from '@angular/core';
import {OnDestroyMixin, untilComponentDestroyed} from '@w11k/ngx-componentdestroyed';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {debounceTime} from 'rxjs/operators';
import {BehaviorSubject} from 'rxjs';

import {UnitRecipientPaymentsHistoryTableService} from '../unit-recipient-payments-history-display-table/unit-recipient-payments-history-table.service';
import {UnitPaymentCategory} from '../../models/unit-payment-category.enum';
import {RoutingService} from '../../../../../../services/routing.service';
import {Router} from '@angular/router';
import { RecipientReadDto } from 'src/app/shared/models/recipient-read-dto.model';
import { UnitPaymentService } from '../../unit-payment.service';

@Component({
  selector: 'terra-unit-recipient-payments-history-dialog',
  templateUrl: './unit-recipient-payments-history-dialog.component.html',
  styleUrls: ['./unit-recipient-payments-history-dialog.component.scss', '../../../unit-shared.scss'],
  providers: [UnitRecipientPaymentsHistoryTableService, UnitPaymentService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UnitRecipientPaymentsHistoryDialogComponent extends OnDestroyMixin implements OnInit, AfterViewInit {
  pageForm: UntypedFormGroup;

  UnitPaymentCategory = UnitPaymentCategory;

  displayClearButton$ = new BehaviorSubject<boolean>(false);

  get recipientName() {
    if (!this.recipient) {
      return '';
    }

    return `${this.recipient.firstName} ${this.recipient.lastName}`;
  }

  constructor(@Inject(MAT_DIALOG_DATA) public recipient: RecipientReadDto,
              private fb: UntypedFormBuilder,
              private dialogRef: MatDialogRef<UnitRecipientPaymentsHistoryDialogComponent>,
              private paymentsHistoryTableService: UnitRecipientPaymentsHistoryTableService,
              private routingService: RoutingService,
              private paymentService: UnitPaymentService,
              private router: Router) {
    super();
  }

  ngOnInit(): void {
    this.generateForm();
    this.subscribeToFormUpdates();
  }

  ngAfterViewInit(): void {
    this.paymentsHistoryTableService.updateRecipientId(this.recipient.id);
  }

  /**
   * Generates form for search params
   */
  private generateForm() {
    this.pageForm = this.fb.group({
      filter: '',
      fromDate: '',
      toDate: '',
      category: ''
    });
  }

  /**
   * Subscribes to search params updates
   */
  private subscribeToFormUpdates() {
    this.pageForm.controls.filter.valueChanges.pipe(
      untilComponentDestroyed(this),
      debounceTime(300)
    ).subscribe(term => {
      this.paymentsHistoryTableService.updateFilter(term);
    });

    this.pageForm.controls.category.valueChanges.pipe(
      untilComponentDestroyed(this),
      debounceTime(300)
    ).subscribe(category => {
      this.paymentsHistoryTableService.updateCategory(category);
    });

    this.pageForm.valueChanges.pipe(
      untilComponentDestroyed(this),
      debounceTime(300)
    ).subscribe(formValues => {
      const anyValue = !!formValues.filter || !!formValues.fromDate || !!formValues.toDate || !!formValues.category;
      this.displayClearButton$.next(anyValue);
    });
  }

  /**
   * Navigates to create payment page to repeat an existing payment
   * @param paymentId Payment id to repeat
   */
  public repeatPayment(paymentId: number) {
    if(this.paymentService.isGp){
      this.router.navigate([this.routingService.createCrePayment()], {queryParams: { repeat: paymentId }} );
    }
    else{
      this.router.navigate([this.routingService.lpCreateUnitPayment()], {queryParams: { repeat: paymentId }} );
    }
    this.dialogRef.close(true);
  }

  /**
   * Update the selected date range
   */
  public updateDateRange() {
    const fromDate = this.pageForm.controls.fromDate.value;
    const toDate = this.pageForm.controls.toDate.value;
    if (!!fromDate && !!toDate) {
      this.paymentsHistoryTableService.updateDates(fromDate, toDate);
    }
  }

  /**
   * Clear all search params
   */
  public clearSearch() {
    this.pageForm.patchValue({
      filter: '',
      fromDate: '',
      toDate: '',
      category: ''
    });
  }
}
